import React from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react';
import autobind from 'autobind-decorator';
import { computed, observable } from 'mobx';
import InPlaceText from 'components/form/in_place_text';
import InPlaceCheckbox from 'components/form/in_place_checkbox';
import { AppUI } from '@seedlang/state';
import Spinner from 'components/spinner';
import { Link } from 'react-router';
import { xor } from 'lodash';
import BackEndExplainer from 'pages/creator/back_end_explainer';
import NavigationItemPathWarning from './navigation_item_path_warning';
import Alert from 'components/alert';
import Grid from 'components/layout/grid';
import Stack from 'components/layout/stack';

const Wrapper = styled.div`
  margin: 20px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  text-align: start;
  font-size: small;
  padding: 0;
  margin-top: 10px;
`;

@observer
class NavigationItemEdit extends React.Component {
  @observable path = '';

  constructor(props) {
    super(props);
    this.getNavigationItem();
    if (!AppUI.membershipTypeStore.hasIndexData) {
      AppUI.membershipTypeStore.getIndex();
    }
  }

  @autobind getNavigationItem() {
    AppUI.navigationItemStore.getShow({ ids: { navigationItemId: this.props.params.navigationItemId }});
  }

  @autobind onUpdateMembershipType(membershipTypeId) {
    const ary = AppUI.navigationItemStore.showData.membershipTypeIds.split(',');
    const membershipTypeIds = xor(ary, [membershipTypeId]).join(',');
    AppUI.navigationItemStore.showData.set('membershipTypeIds', membershipTypeIds);
    AppUI.navigationItemStore.update({ids: {navigationItemId: this.props.params.navigationItemId}, data: {membershipTypeIds: membershipTypeIds}});
  }

  @autobind onPathChange(value) {
    this.path = value;
    AppUI.navigationItemStore.update({
      ids: { navigationItemId: this.props.params.navigationItemId },
      data: { path: value },
    });
  }

  @computed get navigationItem() {
    return AppUI.navigationItemStore.showData;
  }

  componentDidMount() {
    AppUI.pageStore.getIndex({}, data => {
      AppUI.pageStore.setIndexData(data);
      this.path = this.navigationItem.path;
    });
  }

  @computed get notVisibleToAnyone() {
    return (
      !this.navigationItem.showWhenSignedOut &&
      !this.navigationItem.showToNonMembers &&
      this.navigationItem.membershipTypeIds === ''
    );
  }

  @computed get onlyVisibleToSignedOut() {
    return (
      this.navigationItem.showWhenSignedOut &&
      !this.navigationItem.showToNonMembers &&
      this.navigationItem.membershipTypeIds === ''
    );
  }

  @autobind onSelectAllMembershipTypes() {
    AppUI.navigationItemStore.update({
      ids: { navigationItemId: this.props.params.navigationItemId },
      data: {
        membershipTypeIds: AppUI.membershipTypeStore.indexData.map(item => item.id).join(','),
        showToNonMembers: true,
      },
    });
  }

  @autobind onClearMembershipTypes() {
    AppUI.navigationItemStore.update({
      ids: { navigationItemId: this.props.params.navigationItemId },
      data: {
        membershipTypeIds: '',
        showToNonMembers: false,
      },
    });
  }

  @computed get allMembershipTypesSelected() {
    return AppUI.membershipTypeStore.indexData.map(item => item.id).every(
      id => AppUI.navigationItemStore.showData.membershipTypeIds.includes(id),
    );
  }

  render() {
    if (!AppUI.navigationItemStore.hasShowData) {
      return (
        <Spinner />
      );
    }

    return (
      <Wrapper>
        <div className='breadcrumbs-wrapper'>
          <div className='breadcrumbs'>
            <Link to='creator.site_navigation'>Navigation Items</Link>
            <i className='fa fa-angle-double-right' />
            <div className='current'>
              {this.navigationItem.position} - {this.navigationItem.name}
            </div>
          </div>
        </div>
        <Grid columns={2} gap='sm'>
          <InPlaceText
            legend='Name'
            id={this.navigationItem.id}
            defaultValue={this.navigationItem.name}
            model='navigation_items'
            field='name'
          />
          <fieldset>
            <legend>Path</legend>
            <InPlaceText
              id={this.navigationItem.id}
              defaultValue={this.navigationItem.path}
              value={this.path}
              model='navigation_items'
              field='path'
              onChange={this.onPathChange}
              readOnly={!!this.navigationItem.routeName}
              hideEditable={!!this.navigationItem.routeName}
            />
            {
              !this.navigationItem.routeName &&
                <NavigationItemPathWarning
                  path={this.path}
                />
            }
            {
              this.navigationItem?.externalLink &&
                <InPlaceCheckbox
                  id={this.navigationItem.id}
                  value={this.navigationItem.targetBlank}
                  model='navigation_items'
                  field='target_blank'
                >
                  Open External Link in New Tab
                </InPlaceCheckbox>
            }
          </fieldset>
        </Grid>
        {
          this.notVisibleToAnyone &&
            <Alert background={AppUI.site?.accentColor} style={{ margin: '10px 0', width: '100%' }}>
              This navigation item is not visible to anyone. To change this, select at least one of the options below.
            </Alert>
        }
        {
          this.onlyVisibleToSignedOut &&
            <Alert background={AppUI.site?.accentColor} style={{ margin: '10px 0', width: '100%' }}>
              This navigation item is only visible to signed out users. If you want to change this, select Non-Members or a membership type.
            </Alert>
        }
        {
          AppUI.navigationItemStore.hasOutstandingRequests && <Spinner />
        }
        <Grid columns={3} gap='sm'>
          <fieldset>
            <legend>Show in...</legend>
            <Column>
              <InPlaceCheckbox
                id={this.navigationItem.id}
                value={this.navigationItem.showInHeader}
                model='navigation_items'
                field='show_in_header'
              >
                Header
              </InPlaceCheckbox>
              <InPlaceCheckbox
                id={this.navigationItem.id}
                value={this.navigationItem.showInFooter}
                model='navigation_items'
                field='show_in_footer'
              >
                Footer
              </InPlaceCheckbox>
              <InPlaceCheckbox
                id={this.navigationItem.id}
                value={this.navigationItem.showInUserSettings}
                model='navigation_items'
                field='show_in_user_settings'
              >
                User Settings
              </InPlaceCheckbox>
            </Column>
          </fieldset>
          <fieldset>
            <legend>Show To</legend>
            <Column>
              <InPlaceCheckbox
                id={this.navigationItem.id}
                value={this.navigationItem.showWhenSignedOut}
                model='navigation_items'
                field='show_when_signed_out'
                afterChange={this.getNavigationItem}
              >
                Signed Out Users
              </InPlaceCheckbox>
            </Column>
          </fieldset>
          <fieldset>
            <legend>Membership Types when Signed In</legend>
            <Column>
              <InPlaceCheckbox
                id={this.navigationItem.id}
                value={this.navigationItem.showToNonMembers}
                model='navigation_items'
                field='show_to_non_members'
                afterChange={this.getNavigationItem}
              >
                Non-Members
              </InPlaceCheckbox>
              <Stack gap='xs'>
                {
                  AppUI.membershipTypeStore.indexData.map(item => {
                    return (
                      <div key={item.id}>
                        <InPlaceCheckbox
                          value={AppUI.navigationItemStore.showData.membershipTypeIds.includes(item.id)}
                          onChange={() => this.onUpdateMembershipType(item.id)}
                        >
                          {item.name}
                        </InPlaceCheckbox>
                      </div>
                    );
                  })
                }
              </Stack>
              <LinkButton onClick={this.allMembershipTypesSelected ? this.onClearMembershipTypes : this.onSelectAllMembershipTypes}>
                {this.allMembershipTypesSelected ? 'Clear' : 'Select All'}
              </LinkButton>
            </Column>
          </fieldset>
        </Grid>
        {
          this.navigationItem.path.startsWith('/pages') &&
            <BackEndExplainer>
              <div>Filtering of navigation items is independent from the page set up. A page can be visible in the navigation, but its content can be blocked for a given user, and vice versa.</div>
              <div>You can set up pages' visibility in the <Link className='underline' to='creator.site_pages'>Pages</Link> section.</div>
            </BackEndExplainer>
        }
      </Wrapper>
    );
  }
}

export default NavigationItemEdit;